import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { podeAcessarAgenteEstacao } from '../utils/acessoAgente';
import { Box, CircularProgress } from '@mui/material';
import MainLayout from '../components/layout/MainLayout';

// Páginas públicas carregadas sob demanda (lazy)
const LoginPage = lazy(() => import('../pages/LoginPage'));
const RegisterPage = lazy(() => import('../pages/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('../pages/ForgotPasswordPage'));
const RedefinirSenhaPage = lazy(() => import('../pages/RedefinirSenhaPage'));
const AtualizarCartaoPage = lazy(() => import('../pages/AtualizarCartaoPage'));

// Páginas protegidas carregadas sob demanda (lazy)
const DashboardPage = lazy(() => import('../pages/DashboardPage'));
const WhatsAppPage = lazy(() => import('../pages/WhatsAppPage'));
const ClientesPage = lazy(() => import('../pages/ClientesPage'));
const PerfilPage = lazy(() => import('../pages/PerfilPage'));
const FaturasPage = lazy(() => import('../pages/FaturasPage'));
const ClienteDetalhesPage = lazy(() => import('../pages/ClienteDetalhesPage'));
const UsuariosPage = lazy(() => import('../pages/UsuariosPage'));
const UsuarioEditarPage = lazy(() => import('../pages/UsuarioEditarPage'));
const ConfiguracoesPage = lazy(() => import('../pages/ConfiguracoesPage'));
const RepresentantesPage = lazy(() => import('../pages/RepresentantesPage'));
const ComissoesRepresentantePage = lazy(() => import('../pages/comissoes/ComissoesRepresentantePage'));
const HistoricoComissoesPage = lazy(() => import('../pages/comissoes/HistoricoComissoesPage'));
const ComissoesFinanceiroPage = lazy(() => import('../pages/comissoes/ComissoesFinanceiroPage'));
const AtendimentosPage = lazy(() => import('../pages/atendimentos/AtendimentosPage'));
const RelatorioFaturasPage = lazy(() => import('../pages/RelatorioFaturasPage'));
const DownloadsPage = lazy(() => import('../pages/DownloadsPage'));
const CloudflareDomainsPage = lazy(() => import('../pages/CloudflareDomainsPage'));
const UpdaterInstallationsPage = lazy(() => import('../pages/UpdaterInstallationsPage'));
const AgenteEstacaoPage = lazy(() => import('../pages/AgenteEstacaoPage'));

const PageFallback = () => (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress color="primary" />
    </Box>
);

const ProtectedRoute = ({ children, adminOnly, podeAcessar }: { children: React.ReactNode, adminOnly?: boolean, podeAcessar?: (user: any) => boolean }) => {
    const { isAuthenticated, loading, user } = useAuth();

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress color="primary" />
            </Box>
        );
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    if (adminOnly && user?.role !== 'ADMIN') {
        return <Navigate to="/" replace />;
    }

    if (podeAcessar && !podeAcessar(user)) {
        return <Navigate to="/" replace />;
    }

    return (
        <MainLayout>
            <Suspense fallback={<PageFallback />}>
                {children}
            </Suspense>
        </MainLayout>
    );
};

export const AppRoutes = () => {
    return (
        <Routes>
            {/* Rotas Públicas */}
            <Route path="/login" element={<Suspense fallback={<PageFallback />}><LoginPage /></Suspense>} />
            <Route path="/registrar" element={<Suspense fallback={<PageFallback />}><RegisterPage /></Suspense>} />
            <Route path="/recuperar-senha" element={<Suspense fallback={<PageFallback />}><ForgotPasswordPage /></Suspense>} />
            <Route path="/redefinir-senha" element={<Suspense fallback={<PageFallback />}><RedefinirSenhaPage /></Suspense>} />
            <Route path="/atualizar-cartao/:token" element={<Suspense fallback={<PageFallback />}><AtualizarCartaoPage /></Suspense>} />

            {/* Rotas Protegidas */}
            <Route
                path="/"
                element={
                    <ProtectedRoute>
                        <DashboardPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/clientes"
                element={
                    <ProtectedRoute>
                        <ClientesPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/clientes/:id"
                element={
                    <ProtectedRoute>
                        <ClienteDetalhesPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/usuarios"
                element={
                    <ProtectedRoute>
                        <UsuariosPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/usuarios/:id"
                element={
                    <ProtectedRoute>
                        <UsuarioEditarPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/configuracoes"
                element={
                    <ProtectedRoute>
                        <ConfiguracoesPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/whatsapp"
                element={
                    <ProtectedRoute>
                        <WhatsAppPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/faturas/:clienteId"
                element={
                    <ProtectedRoute>
                        <FaturasPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/relatorios/faturas"
                element={
                    <ProtectedRoute>
                        <RelatorioFaturasPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/representantes"
                element={
                    <ProtectedRoute adminOnly={true}>
                        <RepresentantesPage />
                    </ProtectedRoute>
                }
            />

            <Route
                path="/atendimentos"
                element={
                    <ProtectedRoute>
                        <AtendimentosPage />
                    </ProtectedRoute>
                }
            />

            <Route
                path="/downloads"
                element={
                    <ProtectedRoute>
                        <DownloadsPage />
                    </ProtectedRoute>
                }
            />

            {/* Rotas de Comissões */}
            <Route
                path="/comissoes/representante"
                element={
                    <ProtectedRoute>
                        <ComissoesRepresentantePage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/comissoes/historico"
                element={
                    <ProtectedRoute>
                        <HistoricoComissoesPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/comissoes/financeiro"
                element={
                    <ProtectedRoute>
                        <ComissoesFinanceiroPage />
                    </ProtectedRoute>
                }
            />

            <Route
                path="/cloudflare-domains"
                element={
                    <ProtectedRoute adminOnly={true}>
                        <CloudflareDomainsPage />
                    </ProtectedRoute>
                }
            />

            <Route
                path="/agente"
                element={
                    <ProtectedRoute podeAcessar={podeAcessarAgenteEstacao}>
                        <AgenteEstacaoPage />
                    </ProtectedRoute>
                }
            />

            <Route
                path="/updater-installations"
                element={
                    <ProtectedRoute>
                        <UpdaterInstallationsPage />
                    </ProtectedRoute>
                }
            />

            {/* Fallback */}
            <Route
                path="/perfil"
                element={
                    <ProtectedRoute>
                        <PerfilPage />
                    </ProtectedRoute>
                }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
};
